/**
 * Perf instrumentation for the 3D fuel layer (issue #276, phase 0).
 *
 * Every pipeline stage is wrapped in performance.mark/measure (visible in the
 * DevTools Performance panel under "fuel3d:*") and mirrored into a tiny stats
 * store the dev HUD reads. Enabled with `?perf3d` in the URL or
 * `localStorage["fisci.perf3d"] = "1"` — zero overhead otherwise beyond a
 * boolean check.
 */

export const perfEnabled = (() => {
  try {
    return (
      new URLSearchParams(window.location.search).has("perf3d") ||
      localStorage.getItem("fisci.perf3d") === "1"
    );
  } catch {
    return false;
  }
})();

export type PerfStats = {
  /** Stage durations (ms) from the most recent occurrence. */
  timings: Record<string, number>;
  /** Instant values (instance counts, draw calls, heap MB, …). */
  gauges: Record<string, number>;
};

const stats: PerfStats = { timings: {}, gauges: {} };
const listeners = new Set<() => void>();
let snapshot: PerfStats = { timings: {}, gauges: {} };

const notify = () => {
  snapshot = { timings: { ...stats.timings }, gauges: { ...stats.gauges } };
  listeners.forEach((l) => l());
};

/** Time a stage: const end = p3dSpan("rebuild.plant"); …; end(); */
export function p3dSpan(name: string): () => number {
  if (!perfEnabled) return () => 0;
  const start = performance.now();
  return () => {
    const ms = performance.now() - start;
    p3dRecord(name, ms);
    return ms;
  };
}

/** Record an externally measured duration (e.g. worker-side planting). */
export function p3dRecord(name: string, ms: number) {
  if (!perfEnabled) return;
  try {
    performance.measure(`fuel3d:${name}`, {
      start: performance.now() - ms,
      duration: ms,
    });
  } catch {
    /* measure options unsupported — stats store still updates */
  }
  stats.timings[name] = ms;
  notify();
}

export function p3dGauge(name: string, value: number) {
  if (!perfEnabled) return;
  stats.gauges[name] = value;
  notify();
}

export function subscribePerf(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getPerfSnapshot(): PerfStats {
  return snapshot;
}
